import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { VestingHistory } from '@apeiron/reward'
import {
  ApeironEllipsisTypography,
  ApeironPagingDataGrid,
  ApeironPagingDataGridRef,
  NextLink,
  PagingDatasourceResult,
  useChainSwitcher,
  useDataGridContext
} from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import ApeironCrossBackground from '@src/components/share/apeiron/ApeironCrossBackground'
import ApeironSectionTitle from '@src/components/share/apeiron/ApeironSectionTitle'
import { DATE_DISPLAY_FORMAT_WITHOUT_TIME } from '@src/constants/date'
import useGetVestingHistories from '@src/hooks/graphql/useGetVestingHistories'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { format } from 'date-fns'
import * as R from 'ramda'
import { Ref, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const StyledApeironCrossBackground = styled(ApeironCrossBackground)`
  .Custom-Content {
    padding: 16px;
  }
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`

const StyledApeironPagingDataGrid = styled(ApeironPagingDataGrid)`
  .Custom-Grid {
    border-radius: unset;

    .ag-header {
      font-weight: 300;
      font-size: 16px;
      margin-bottom: -1px;
    }

    .ag-header-row {
      background-color: #151615;
    }

    .ag-cell {
      font-size: 16px;
    }
  }
`

const Link = styled(NextLink)`
  width: 100%;
  :hover {
    p {
      color: #ffb752;
      text-decoration: underline;
    }
  }
`

const EllipsisText = styled(ApeironEllipsisTypography)`
  font-size: 16px;
  font-weight: 300;
  text-align: center;
  width: 100%;
`

const VestingHistorySection = (props: Props) => {
  const { gridRef } = props

  const { t } = useTranslation()

  const { fetch } = useGetVestingHistories()

  const { getApeironPrice } = useCurrencyContext()

  const { showGrid } = useDataGridContext()

  const { getTransactionUrl } = useChainSwitcher()

  const columns = useMemo((): ColDef<any, VestingHistory>[] => {
    return [
      {
        field: 'start',
        headerName: '',
        pinned: 'left',
        width: 30
      },
      {
        field: 'amount',
        flex: 0.2,
        headerClass: 'custom-ag-header-left',
        headerName: t('vesting.amount'),
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<VestingHistory>) => {
          const history = R.prop('data', params)

          if (R.isNil(history)) {
            return <></>
          }

          const price = getApeironPrice(
            R.propOr(CurrencyTokenType.Unspecified, 'currency', history),
            history.amount
          )

          return price
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'claimedAt',
        flex: 0.3,
        headerClass: 'custom-ag-header-center',
        headerName: t('vesting.claimed_at'),
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams<VestingHistory>) => {
          const history = R.prop('data', params)

          if (R.isNil(history)) {
            return <></>
          }

          const claimedAt = history.claimedAt

          return format(new Date(claimedAt), DATE_DISPLAY_FORMAT_WITHOUT_TIME)
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'amount',
        flex: 0.5,
        headerClass: 'custom-ag-header-center',
        headerName: t('vesting.transaction'),
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<VestingHistory>) => {
          const history = R.prop('data', params)

          if (R.isNil(history)) {
            return <></>
          }

          const url = getTransactionUrl(
            history.network,
            history.transactionHash
          )

          return (
            <Link href={url} target='_blank'>
              <EllipsisText>{history.transactionHash}</EllipsisText>
            </Link>
          )
        }
      }
    ]
  }, [getApeironPrice, getTransactionUrl, t])

  const handleOnFetch = useCallback(
    async (page: number, pageSize: number): Promise<PagingDatasourceResult> => {
      const result = await fetch({
        pageInput: {
          page,
          pageSize
        }
      })

      return {
        data: result.histories,
        total: result.pageInfo?.total || 0
      }
    },
    [fetch]
  )

  return showGrid ? (
    <>
      <ApeironSectionTitle title={t('vesting.claim_history')} />
      <StyledApeironCrossBackground>
        <Container>
          <StyledApeironPagingDataGrid
            columnDefs={columns}
            gridRef={gridRef}
            headerHeight={60}
            pageSize={20}
            rowHeight={60}
            fetch={handleOnFetch}
          />
        </Container>
      </StyledApeironCrossBackground>
    </>
  ) : (
    <></>
  )
}

type Props = {
  gridRef?: Ref<ApeironPagingDataGridRef>
}

export default VestingHistorySection
